import { mapValues } from 'lodash';

import { cms_PublicPage, Listing } from './rql';
import { CmsPageType } from './constants';

import template from '@reverbdotcom/url-template';
import { useUser } from './user_hooks';
import { IUrl } from './url_context';
import { CORE_WEB_ORIGIN, CMS_ADMIN_URL } from '@reverbdotcom/env';

const DEFAULT_UPLOAD_IMAGE_CDN = 'https://res.cloudinary.com/reverb-cms';

// the second argument here is temporary until we unify CDN paths between CMS and LP
export function imageUploadURL(image_key, cdn = DEFAULT_UPLOAD_IMAGE_CDN) {
  const path = template.parse('/image/upload/{image_key}').expand({ image_key });
  return `${cdn}${path}`;
}

export const YOUTUBE_IMAGE = template.parse('https://img.youtube.com/vi/{slug}/hqdefault.jpg');
export const YOUTUBE_VIDEO_STATS_API = template.parse<{
  part: string;
  id: string;
  key: string;
}>('https://youtube.googleapis.com/youtube/v3/videos{?part,id,key}');

export interface YouTubeVideoContext {
  slug: string;
  autoplay?: boolean;
}

export const YOUTUBE_VIDEO = template.parse<YouTubeVideoContext>('https://www.youtube.com/embed/{slug}{?autoplay}');
export const VIMEO_VIDEO = template.parse<{ slug: string }>('https://player.vimeo.com/video/{slug}');
const HELP_CENTER_ARTICLE = template.parse<{
  articleId: string;
  locale: string;
}>('https://help.reverb.com/hc/{locale}/articles/{articleId}');

const HELP_CENTER_SECTION = template.parse<{
  sectionId: string;
  locale: string;
}>('https://help.reverb.com/hc/{locale}/sections/{sectionId}');

/**
 * @param articleId
 * @returns Locale-aware Help Center Article URL
 */
export function useHelpCenterArticleUrl(articleId: string) {
  const { locale } = useUser();
  return HELP_CENTER_ARTICLE.expand({ locale, articleId });
}

/**
 * @param sectionId
 * @returns Locale-aware Help Center Section URL
 */
export function useHelpCenterSectionUrl(sectionId: string) {
  const { locale } = useUser();
  return HELP_CENTER_SECTION.expand({ locale, sectionId });
}

export interface ItemWithSlugRouteContext {
  id: string | number;
  slug: string;
  show_sold?: boolean;
  bk?: string; // bump key
  rollup?: boolean;
}

export const ITEM_WITH_SLUG_ROUTE =
  template.parse<ItemWithSlugRouteContext>('/item/{id}-{slug}{?show_sold,bk,rollup}');

export interface BrandRouteContext {
  brand_slug: string;
  product_type?: string;
  category?: string;
}
export const BRAND_ROUTE = template.parse<BrandRouteContext>('/brand/{brand_slug}{?product_type,category}');

export const SHOP_PATH = template.parse<{ slug: string }>('/shop/{slug}');

export function cmsEditRoute(uuid: string): string {
  const path = template.parse('/pages/{uuid}/edit').expand({ uuid });
  return `${CMS_ADMIN_URL}${path}`;
}

export interface OAuthLoginContext {
  redirect_to?: string;
}

export const APPLE_AUTH_PATH = template.parse<OAuthLoginContext>('/auth/apple{?redirect_to}');

export const GOOGLE_AUTH_PATH = template.parse<OAuthLoginContext>('/auth/google{?redirect_to}');

export const PASSWORD_RESET_PATH = '/forgot_password';

export const FACEBOOK_AUTH_PATH = '/auth/facebook';

export const TERMS_OF_USE_PATH = '/page/terms';

export const PRIVACY_POLICY_PATH = '/page/privacy';

export const GDPR_POLICY_PATH = '/page/eu-data-transfer-GDPR';

export enum CmsPrefix {
  C = 'c',
  SELLING = 'selling',
  SALE = 'sale',
  GUIDE = 'guide',
  GEAR = 'gear',
  SEO_LANDING_PAGE = 's',
  LEGAL = 'legal',
  PRESS = 'press',
  PROMO = 'promo',
  FEATURED = 'featured',
}

export const CmsPageTypeRoutes: Partial<Record<CmsPageType, CmsPrefix>> = {
  [CmsPageType.SALE]: CmsPrefix.SALE,
  [CmsPageType.BUYING_GUIDE]: CmsPrefix.GUIDE,
  [CmsPageType.CATEGORY]: CmsPrefix.C,
  [CmsPageType.SELLING]: CmsPrefix.SELLING,
  [CmsPageType.GEAR]: CmsPrefix.GEAR,
  [CmsPageType.LEGAL]: CmsPrefix.LEGAL,
  [CmsPageType.PRESS]: CmsPrefix.PRESS,
  [CmsPageType.PROMO]: CmsPrefix.PROMO,
  [CmsPageType.FEATURED]: CmsPrefix.FEATURED,
  [CmsPageType.SEO_LANDING_PAGE]: CmsPrefix.SEO_LANDING_PAGE,
};

interface CmsPagePathContext {
  prefix: string;
  slug?: string;
  childSlugs?: string[];
}

const CMS_PAGE_PATH = template.parse<CmsPagePathContext>('/{prefix}{/slug}{/childSlugs*}');
const CMS_PATH_WITH_TRAILING_SLASH = template.parse<CmsPagePathContext>('/{prefix}/{slug}/{childSlugs*}');

export function cmsPagePath(page: cms_PublicPage) {
  if (page?.pageType === CmsPageType.ROOT) {
    return CMS_PAGE_PATH.expand({ prefix: page.slug });
  }

  const path = page?.pageType === CmsPageType.CATEGORY ? CMS_PATH_WITH_TRAILING_SLASH : CMS_PAGE_PATH;
  const fullSlug = page?.slug || '';
  const [slug, ...childSlugs] = fullSlug.split('/');
  const prefix = CmsPageTypeRoutes[page?.pageType] || CmsPrefix.C;

  return path.expand({
    slug,
    childSlugs,
    prefix,
  });
}

export function cmsPageUrl(page: cms_PublicPage) {
  return `${CORE_WEB_ORIGIN}${cmsPagePath(page)}`;
}

export enum SupportedUrlRegion {
  AU = 'au',
  CA = 'ca',
  DE = 'de',
  ES = 'es',
  FR = 'fr',
  IT = 'it',
  JP = 'jp',
  UK = 'uk',
}

export const SUPPORTED_URL_REGIONS = Object.values(SupportedUrlRegion) as string[];

export function regionalizePath(path: string, urlRegion: string) {
  const [preSlash, firstSegment, ...segments] = path.split('/');

  if (preSlash !== '') { return path; }

  // Special case for `/`
  if (!firstSegment && !urlRegion) { return path; }

  if (firstSegment && !SUPPORTED_URL_REGIONS.includes(firstSegment)) {
    segments.unshift(firstSegment);
  }

  if (urlRegion && SUPPORTED_URL_REGIONS.includes(urlRegion)) {
    segments.unshift(urlRegion);
  }

  return ['', ...segments].join('/');
}

export function newDashboardMessagesPath(listingId, recipientUuid) {
  return template.parse('/my/messages/new{?item,recipient_uuid}').expand({
    item: listingId,
    recipient_uuid: recipientUuid,
  });
}

export function prefilledListingFromCSP(cspId) {
  return template.parse('/sell_prefilled{?seed_id}').expand({ seed_id: cspId });
}

export function prefilledListingFromListing(listingId) {
  return template.parse('/sell_prefilled{?listing}').expand({ listing: listingId });
}

export function listingItemPagePath(listing: Listing): string {
  const { id, slug, bumpKey } = listing;

  return ITEM_WITH_SLUG_ROUTE.expand({ id, slug, bk: bumpKey?.key });
}

// Usage: `Paths.buyerOffer.expand({ offerId, status })`
export const Paths = mapValues({
  marketplace: '/marketplace{?product_type,category,make,query,condition,csp_slug,trait_values,year_max}',
  newsletterSignup: '/mailchimp_subscriptions?list_id=',
  emailNewsletterSignup: '/email_subscriptions',
  sellGear: 'sell/search',
  filteredCsp: '/p/{cspSlug}{?deals_and_steals,on_sale,free_shipping,condition,zero_percent_financing}',
  orderBundlesClaim: '/order_bundles/{orderBundleUuid}/claim',
}, (v) => template.parse(v));

export function isReferredFromGoogle(url: Partial<IUrl>) {
  return /google\./.test(url.referer || '');
}

export function newsletterSignupPath() {
  return Paths.newsletterSignup.expand({});
}

export function emailNewsletterSignupPath() {
  return Paths.emailNewsletterSignup.expand({});
}

const BRAND_URL_PATTERN = '/brand/';
const ITEM_URL_PATTERN = '/item/';
const SHOP_URL_PATTERN = '/shop/';
const CSP_URL_PATTERN = '/p/';
const PRODUCT_FAMILY_URL_PATTERN = '/f/';
const CATEGORY_URL_PATTERN = '/c/';
const MARKETPLACE_URL_PATTERN = '/marketplace';

export function isBrandPage(url: Partial<IUrl>) {
  return (url.pathname || '').includes(BRAND_URL_PATTERN);
}

export function isItemPage(url: Partial<IUrl>) {
  return (url.pathname || '').includes(ITEM_URL_PATTERN);
}

export function isShopPage(url: Partial<IUrl>) {
  return (url.pathname || '').includes(SHOP_URL_PATTERN);
}

export function isCSP(url: Partial<IUrl>) {
  return (url.pathname || '').includes(CSP_URL_PATTERN);
}

export function isProductFamilyPage(url: Partial<IUrl>) {
  return (url.pathname || '').includes(PRODUCT_FAMILY_URL_PATTERN);
}

export function isCategoryPage(url: Partial<IUrl>) {
  return (url.pathname || '').includes(CATEGORY_URL_PATTERN);
}

export function isMarketplacePage(url: Partial<IUrl>) {
  return (url.pathname || '').includes(MARKETPLACE_URL_PATTERN);
}

export function isRegionalURL(url: Partial<IUrl>, region: string) {
  if (!SUPPORTED_URL_REGIONS.includes(region)) {
    return false;
  }
  const path = url.pathname || '';
  const firstSegment = path.split('/')[1];
  return firstSegment === region;
}

export function isUsingProximityFeatures(url: Partial<IUrl>) {
  const search = url?.search || '';
  return search.includes('proximity=true') ||
    search.includes('local_pickup=true') ||
    search.includes('sort=proximity');
}

export function isEndedWatchesPath(url: Partial<IUrl>) {
  return !!(url?.pathname || '').match('\/watched_products(\/shared\/[^\/]+)?\/ended$');
}
